import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { TIER } from '../utils/tier-constants';
import { PLANS, PaidTier, DurationMonths, getPlanPrice } from '../config/plans';
import { PlanCard } from './plans/PlanCard';
import { Dialog, SegmentedControl, buttonClasses } from './ui';

type DurationOption = '1' | '12';

/**
 * Compact upsell preview. Duration is fixed to 12 months (cheapest per-month price) with a
 * toggle to 1 month; each card's CTA hands off to the full Upgrade page (with the plan
 * preselected via `?plan=`), which owns the actual checkout call and consent gate.
 */
export const UpgradeModal: React.FC = () => {
  const { isOpen, suggestedTier, closeUpgradeModal } = useUpgradeModal();
  const { user } = useAuth();
  const userTier = user?.tier || TIER.FREE;

  const [monthsOption, setMonthsOption] = useState<DurationOption>('12');
  const months = Number(monthsOption) as DurationMonths;

  if (!isOpen) return null;

  const suggested: PaidTier = suggestedTier ?? (userTier === TIER.PRO ? 'premium' : 'pro');

  const planCta = (tier: PaidTier, label: string) => (
    <Link
      to={`/dashboard/upgrade?plan=${getPlanPrice(tier, months).planId}`}
      className={buttonClasses('primary', 'sm', 'w-full')}
      onClick={closeUpgradeModal}
    >
      {label}
    </Link>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={closeUpgradeModal}
      title="Upgrade wählen"
      description="Mehr Suchen, Filter und schnellere Meldungen."
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <SegmentedControl
          label="Laufzeit"
          showLabel
          value={monthsOption}
          onChange={setMonthsOption}
          options={[
            { value: '12', label: '12 Monate' },
            { value: '1', label: '1 Monat' },
          ]}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <PlanCard
            plan={PLANS.pro}
            months={months}
            compact
            highlighted={suggested === 'pro'}
            current={userTier === TIER.PRO}
            cta={planCta('pro', 'Weiter zu Pro')}
          />
          <PlanCard
            plan={PLANS.premium}
            months={months}
            compact
            highlighted={suggested === 'premium'}
            current={userTier === TIER.PREMIUM}
            cta={planCta('premium', 'Weiter zu Premium')}
          />
        </div>
      </div>
    </Dialog>
  );
};
